import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import homeData from "../../data/home.json";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle,
  Clock,
  Tag,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ExternalLink,
  TrendingUp,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "../../api/api";
import moment from "moment/moment";
import projectDefaultImg from "../../image/Default Project.png";

// format duration from start and end date
function formatDuration(start, end) {
  const startDate = moment(start, "YYYY-MM");
  const endDate = moment(end, "YYYY-MM");

  const totalMonths = endDate.diff(startDate, "months");

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  const parts = [];
  if (years) parts.push(`${years} year${years > 1 ? "s" : ""}`);
  if (months) parts.push(`${months} month${months > 1 ? "s" : ""}`);

  return parts.join(" ");
}

/* ─────────────────────────────────────────────
 OUTCOME ITEM
───────────────────────────────────────────── */

const OutcomeItem = ({ text, color, delay = 0 }) => (
  <div className="outcome-item flex items-center gap-2.5 text-sm text-slate-700">
    <div
      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ background: `${color}25` }}
    >
      <CheckCircle size={11} style={{ color }} />
    </div>
    <span>{text}</span>
  </div>
);

/* ─────────────────────────────────────────────
 FEATURED PROJECT CARD (large — first 2)
───────────────────────────────────────────── */
const FeaturedCard = ({ project, index, isActive, onActivate }) => {
  const cardRef = useRef(null);
  const imgRef = useRef(null);
  const overlayRef = useRef(null);
  const numRef = useRef(null);
  const infoRef = useRef(null);
  const outRef = useRef(null);
  const lineRef = useRef(null);

  /* Scroll reveal */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        {
          y: 80,
          opacity: 0,
          clipPath: "inset(0 0 100% 0)",
        },
        {
          y: 0,
          opacity: 1,
          clipPath: "inset(0 0 0% 0)",
          duration: 1,
          delay: index * 0.2,
          ease: "power4.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 80%",
          },
        },
      );
    }, cardRef);
    return () => ctx.revert();
  }, [index]);

  /* Hover */
  const handleEnter = useCallback(() => {
    onActivate(project.id);
    gsap.to(imgRef.current, {
      scale: 1.06,
      duration: 0.8,
      ease: "power2.out",
    });
    gsap.to(overlayRef.current, {
      opacity: 1,
      duration: 0.4,
    });
    gsap.to(numRef.current, {
      opacity: 0.15,
      scale: 1.1,
      duration: 0.4,
    });
    gsap.fromTo(
      outRef.current?.querySelectorAll(".outcome-item") ?? [],
      { x: -15, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        stagger: 0.08,
        duration: 0.4,
        ease: "power3.out",
      },
    );
    gsap.to(lineRef.current, {
      scaleX: 1,
      duration: 0.5,
      ease: "power3.out",
    });
  }, [onActivate, project.id]);

  const handleLeave = useCallback(() => {
    gsap.to(imgRef.current, {
      scale: 1,
      duration: 0.6,
      ease: "power2.out",
    });
    gsap.to(overlayRef.current, {
      opacity: 0.5,
      duration: 0.4,
    });
    gsap.to(numRef.current, {
      opacity: 0.06,
      scale: 1,
      duration: 0.3,
    });
    gsap.to(lineRef.current, {
      scaleX: 0,
      duration: 0.3,
      ease: "power2.in",
    });
  }, []);

  return (
    <div
      ref={cardRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-cursor="card"
      className="featured-card relative rounded-3xl overflow-hidden cursor-pointer
 group bg-white shadow-lg"
      style={{
        opacity: 0,
        aspectRatio: "16/9",
        border: `1px solid ${project.color}25`,
      }}
    >
      {/* Image */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          ref={imgRef}
          src={
            project.image
              ? `${process.env.REACT_APP_BACKEND_URL}${project.image}`
              : projectDefaultImg
          }
          alt={project.title}
          className="w-full h-full object-cover will-change-transform"
          loading="lazy"
        />
      </div>

      {/* Gradient overlay */}
      <div
        ref={overlayRef}
        className="absolute inset-0"
        style={{
          background: `
 linear-gradient(to top,
 rgba(0,0,0,0.8) 0%,
 rgba(0,0,0,0.4) 40%,
 rgba(0,0,0,0.1) 100%
 )
`,
          opacity: 0.5,
        }}
      />

      {/* Number watermark */}
      <div
        ref={numRef}
        className="absolute top-4 right-6 font-black font-mono
 leading-none pointer-events-none select-none"
        style={{
          fontSize: "8rem",
          color: project.color,
          opacity: 0.06,
          WebkitTextStroke: `2px ${project.color}`,
          WebkitTextFillColor: "transparent",
        }}
      >
        {project.index}
      </div>

      {/* Top badges */}
      <div className="absolute top-5 left-5 flex items-center gap-2 z-10">
        <span className="section-label text-[10px] py-1 px-2.5">
          {project.category}
        </span>
        {project.is_featured && (
          <span
            className="flex items-center gap-1 text-[10px] font-semibold
 px-2.5 py-1 rounded-full border"
            style={{
              color: project.color,
              borderColor: `${project.color}40`,
              background: `${project.color}15`,
            }}
          >
            <TrendingUp size={9} />
            Featured
          </span>
        )}
      </div>

      {/* Year badge */}
      <div className="absolute top-5 right-5 z-10">
        <span
          className="text-[10px] font-mono glass px-2.5 py-1
 rounded-full border border-white/10 text-slate-400"
        >
          {moment(project.start_date).format("YYYY")}
        </span>
      </div>

      {/* Bottom content */}
      <div
        ref={infoRef}
        className="absolute bottom-0 left-0 right-0 p-6 z-10 space-y-4"
      >
        {/* Client */}
        <div className="flex items-center gap-2">
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: project.color }}
          />
          <span
            className="text-xs font-semibold tracking-wider uppercase"
            style={{ color: project.color }}
          >
            {project.client}
          </span>
        </div>

        {/* Title */}
        <h3
          className="font-display font-bold text-slate-900 leading-snug"
          style={{ fontSize: "clamp(1.1rem, 2vw, 1.4rem)" }}
        >
          {project.title}
        </h3>

        {/* Divider line */}
        <div
          ref={lineRef}
          className="h-px rounded-full origin-left"
          style={{
            background: `linear-gradient(to right, ${project.color}60, transparent)`,
            transform: "scaleX(0)",
          }}
        />

        {/* Outcomes */}
        <div ref={outRef} className="grid grid-cols-2 gap-2">
          {project.extended_data.outcomes.map((out, i) => (
            <OutcomeItem
              key={i}
              text={out}
              color={project.color}
              delay={i * 0.08}
            />
          ))}
        </div>

        {/* Tags + duration row */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1.5">
            {project.tags.map((tag, i) => (
              <span key={i} className="tag text-[10px] py-0.5 px-2">
                <Tag size={8} />
                {tag}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600 flex-shrink-0">
            <Clock size={11} className="text-primary-500" />
            {formatDuration(project.start_date, project.end_date)}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 COMPACT PROJECT CARD (smaller — last 2)
───────────────────────────────────────────── */
const CompactCard = ({ project, index, onActivate }) => {
  const cardRef = useRef(null);
  const imgRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        { x: 40, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.7,
          delay: index * 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 82%",
          },
        },
      );
    }, cardRef);
    return () => ctx.revert();
  }, [index]);

  const handleEnter = () => {
    onActivate(project.id);
    gsap.to(imgRef.current, {
      scale: 1.06,
      duration: 0.6,
      ease: "power2.out",
    });
    gsap.to(lineRef.current, {
      scaleX: 1,
      duration: 0.4,
      ease: "power3.out",
    });
  };

  const handleLeave = () => {
    gsap.to(imgRef.current, {
      scale: 1,
      duration: 0.5,
      ease: "power2.out",
    });
    gsap.to(lineRef.current, {
      scaleX: 0,
      duration: 0.3,
    });
  };

  return (
    <div
      ref={cardRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="compact-card relative rounded-2xl overflow-hidden cursor-pointer
 flex flex-col sm:flex-row gap-0 bg-white shadow-md border border-slate-200"
      style={{
        opacity: 0,
      }}
    >
      {/* Image */}
      <div
        className="relative overflow-hidden flex-shrink-0"
        style={{
          width: "100%",
          height: "160px",
          ...(window.innerWidth >= 640 && { width: "200px", height: "auto" }),
        }}
      >
        <img
          ref={imgRef}
          src={`${process.env.REACT_APP_BACKEND_URL}${project.image}`}
          alt={project.title}
          className="w-full h-full object-cover will-change-transform"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg,
 rgba(0,0,0,0.4) 0%,
 rgba(0,0,0,0.1) 100%)`,
          }}
        />
        {/* Number */}
        <div
          className="absolute bottom-3 left-3 font-black font-mono text-3xl
 leading-none pointer-events-none select-none"
          style={{
            color: project.color,
            WebkitTextStroke: `1px ${project.color}`,
            WebkitTextFillColor: "transparent",
            opacity: 0.5,
          }}
        >
          {project.index}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-5 space-y-3 min-w-0">
        {/* Category + client */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="section-label text-[9px] py-0.5 px-2">
            {project.category}
          </span>
          <span
            className="text-[10px] font-semibold"
            style={{ color: project.color }}
          >
            {project.client}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-bold text-slate-900 text-sm leading-snug line-clamp-2">
          {project.title}
        </h3>

        {/* Animated line */}
        <div
          ref={lineRef}
          className="h-[1px] rounded-full origin-left"
          style={{
            background: `${project.color}50`,
            transform: "scaleX(0)",
          }}
        />

        {/* Meta */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <Clock size={10} className="text-primary-500" />
            <span>{formatDuration(project.start_date, project.end_date)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <Calendar size={10} className="text-secondary-500" />
            <span>{moment(project.start_date).format("YYYY")}</span>
          </div>
        </div>

        {/* Outcomes preview */}
        <div className="space-y-1.5">
          {project?.extended_data.outcomes?.slice(0, 2).map((out, i) => (
            <div
              key={i}
              className="flex items-center gap-2 text-xs text-slate-600"
            >
              <div
                className="w-1 h-1 rounded-full flex-shrink-0"
                style={{ background: project.color }}
              />
              {out}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 SECTION HEADER
───────────────────────────────────────────── */
const SectionHeader = ({ data }) => {
  const labelRef = useRef(null);
  const headRef = useRef(null);
  const subRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: labelRef.current,
          start: "top 82%",
        },
      });
      tl.fromTo(
        labelRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
      )
        .fromTo(
          headRef.current?.children ?? [],
          { y: 45, opacity: 0, skewY: 2 },
          {
            y: 0,
            opacity: 1,
            skewY: 0,
            stagger: 0.12,
            duration: 0.8,
            ease: "power4.out",
          },
          "-=0.2",
        )
        .fromTo(
          lineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.6,
            ease: "power3.out",
            transformOrigin: "left",
          },
          "-=0.3",
        )
        .fromTo(
          subRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.3",
        );
    });
    return () => ctx.revert();
  }, []);

  return (
    <div
      className="flex flex-col lg:flex-row lg:items-end
 justify-between gap-6 mb-12"
    >
      <div className="space-y-4 max-w-xl">
        <div ref={headRef} className="overflow-hidden space-y-1">
          <h2
            className="font-display font-black text-slate-900 leading-tight"
            style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.5rem)" }}
          >
            {data.headline}
          </h2>
          <h2
            className="font-display font-black text-gradient-primary leading-tight"
            style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.5rem)" }}
          >
            {data.headlineAccent}
          </h2>
        </div>
        <div
          ref={lineRef}
          className="h-[2px] w-16 rounded-full"
          style={{
            background: "linear-gradient(90deg, #1d4ed8, #f59e0b)",
            transform: "scaleX(0)",
          }}
        />
        <p
          ref={subRef}
          className="text-slate-600 text-[15px] leading-relaxed"
          style={{ opacity: 0 }}
        >
          {data.subheadline}
        </p>
      </div>

      {/* View all CTA */}
      <Link
        to={data.cta.href}
        className="group inline-flex items-center gap-2.5 px-6 py-3.5
 border border-primary-300 text-primary-600
 hover:bg-primary-50 hover:border-primary-400
 font-semibold rounded-xl text-sm self-start lg:self-auto
 transition-all duration-300 hover:-translate-y-0.5 flex-shrink-0"
      >
        <span>{data.cta.text}</span>
        <ExternalLink
          size={14}
          className="transition-transform duration-300
 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </Link>
    </div>
  );
};

/* ─────────────────────────────────────────────
 PROGRESS DOTS NAVIGATION
───────────────────────────────────────────── */
const ProjectNav = ({ total, active, onSelect }) => (
  <div className="flex items-center gap-2 justify-center mt-10">
    {Array.from({ length: total }).map((_, i) => (
      <button
        key={i}
        onClick={() => onSelect(i)}
        className="transition-all duration-300 rounded-full"
        style={{
          width: i === active ? "24px" : "8px",
          height: "8px",
          background:
            i === active ? "var(--color-secondary)" : "rgba(255,255,255,0.2)",
        }}
        aria-label={`Project ${i + 1}`}
      />
    ))}
  </div>
);

/* ─────────────────────────────────────────────
 MAIN PORTFOLIO SECTION
───────────────────────────────────────────── */
const PortfolioSection = () => {
  const { portfolio } = homeData;
  const [activeId, setActiveId] = useState(portfolio.items[0].id);
  const limit = 5;

  const { data: project } = useQuery({
    queryKey: ["projects", limit],
    queryFn: () =>
      api
        .get("/api/projects/public", {
          params: {
            limit,
            isActive: true,
          },
        })
        .then((res) => res.data),
  });
  const sectionRef = useRef(null);
  const bgRef = useRef(null);

  const featuredProjects = project?.projects.filter((p) => p.is_featured);
  const compactProjects = project?.projects.filter((p) => !p.is_featured);

  /* BG parallax */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(bgRef.current, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const handleActivate = useCallback((id) => setActiveId(id), []);

  return (
    <section
      ref={sectionRef}
      id="portfolio"
      className="relative overflow-hidden pt-5  bg-white"
    >
      {/* BG */}
      <div ref={bgRef} className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-pattern opacity-[0.03]" />
        <div
          className="absolute top-1/3 left-0 w-96 h-96 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        <div
          className="absolute bottom-1/3 right-0 w-80 h-80 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(245,158,11,0.04) 0%, transparent 70%)",
            filter: "blur(50px)",
          }}
        />
      </div>

      {/* Light mode edge fades */}
      <div
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none z-10 opacity-100 transition-opacity duration-500"
        style={{
          background:
            "linear-gradient(to bottom, #ffffff 0%, transparent 100%)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-10 opacity-100 transition-opacity duration-500"
        style={{
          background: "linear-gradient(to top, #ffffff 0%, transparent 100%)",
        }}
      />

      {/* Vertical label */}
      <div
        className="absolute right-4 top-1/2 -translate-y-1/2 hidden xl:flex
 flex-col items-center gap-3 pointer-events-none z-10"
      >
        <div
          className="w-px h-16"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(245,158,11,0.3))",
          }}
        />
        <p
          className="text-[10px] font-semibold tracking-[0.4em] text-slate-400 uppercase"
          style={{ writingMode: "vertical-lr" }}
        >
          Portfolio
        </p>
        <div
          className="w-px h-16"
          style={{
            background:
              "linear-gradient(to top, transparent, rgba(245,158,11,0.3))",
          }}
        />
      </div>

      <div className="container-fluid relative z-20">
        {/* Header */}
        <SectionHeader data={portfolio} />

        {/* ── FEATURED GRID (2 large) ── */}
        {featuredProjects?.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {featuredProjects.map((project, i) => (
              <FeaturedCard
                key={project.id}
                project={project}
                index={i}
                isActive={activeId === project.id}
                onActivate={handleActivate}
              />
            ))}
          </div>
        )}

        {/* ── COMPACT GRID (2 smaller) ── */}
        {compactProjects?.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {compactProjects.map((project, i) => (
              <CompactCard
                key={project.id}
                project={project}
                index={i}
                onActivate={handleActivate}
              />
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-14 text-center space-y-5">
          <p className="text-slate-600 text-sm">
            These represent a selection of our work. Every project is tailored
            to client needs.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/services"
              className="group inline-flex items-center gap-2.5
 px-7 py-4 bg-primary-600 hover:bg-primary-500
 text-white font-semibold rounded-2xl text-sm
 transition-all duration-300 hover:shadow-glow-primary
 hover:-translate-y-0.5 overflow-hidden relative"
            >
              <span
                className="absolute inset-0 translate-x-[-100%]
 group-hover:translate-x-[100%] transition-transform duration-700
 bg-gradient-to-r from-transparent via-white/15 to-transparent"
              />
              <span>Explore Our Services</span>
              <ArrowRight size={15} />
            </Link>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2.5
 px-7 py-4 border border-slate-300 hover:border-slate-400
 text-slate-700 hover:bg-slate-50 font-medium rounded-2xl
 text-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              <span>Start a Project</span>
              <ArrowUpRight size={14} className="text-slate-500" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
